// Lightweight Google Analytics (GA4) wrapper + first-party event ingest.
// Loads gtag only when REACT_APP_GA_ID is set; otherwise GA reporting is a no-op.
// Whitelisted events are ALSO posted to /api/events/track so the /admin/analytics
// dashboard can query them without depending on the GA4 Data API.
const GA_ID = process.env.REACT_APP_GA_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror of backend ALLOWED_EVENT_NAMES — events outside this set are GA-only
// and are NOT sent to the backend (saves a network call + keeps the events
// collection focused on what /admin/analytics actually visualizes).
const FIRST_PARTY_EVENTS = new Set([
  "results_city_callout_click",
  "results_city_callout_hub_click",
  // Affiliate revenue events — mirror to backend so /admin revenue-
  // attribution widget can aggregate without GA4 Data API setup.
  "angi_affiliate_click",
  "angi_affiliate_redirect",
  "angi_affiliate_redirect_manual",
  // Lead funnel — dialog opened (top) + submitted (bottom). Backend
  // computes per-source completion rate for the funnel widget.
  "lead_dialog_opened",
  "lead_submit",
  // A/B-test exposure for lead-submit button copy variant.
  "ab_lead_submit_variant_exposed",
  // Solar Readiness verdict → HELOC comparator click-through. Tracked
  // for admin attribution + later A/B testing of the CTA copy.
  "sr_heloc_cta_click",
  // Same pattern for Basement Finish verdict (STRONG YES / PROBABLY).
  "bf_heloc_cta_click",
  // Same pattern for Renovation ROI verdict (HIGH ROI / MEDIUM ROI) —
  // the most generic feeder (covers all project types).
  "roi_heloc_cta_click",
  // View event on financing comparator when arrived with a known ?source=
  // tag — feeds the admin per-source funnel widget.
  "financing_calc_source_view",
  // State + utility rebate lookup tool — per-state lookup volume +
  // which programs draw the most clicks.
  "rebate_lookup_state_selected",
  "rebate_program_click",
  // Comfort upgrade bundle wizard (HVAC + Furnace + Insulation) — fires when
  // the user finishes the multi-step flow and lands on the bundled estimate.
  "comfort_bundle_estimate_submitted",
  // PDF download of the bundled comfort-upgrade estimate. Tracked so we can
  // measure share/forward intent (homeowners send the PDF to CPA/contractor/
  // spouse — each forward is a brand impression).
  "comfort_bundle_pdf_download",
  // PDF download of a state's smart-home rebate stack from the
  // /state-utility-rebate-lookup tool. Same share-intent signal.
  "rebate_lookup_pdf_download",
]);

let initialized = false;

/**
 * Detect headless / data-center browsers that should NOT count as real users.
 * Blocking is browser-side (proactive) and complements the GA4 admin filter
 * for country/region (which catches server-side-only pings).
 *
 * Signals checked (each individually indicates a bot with ~99%+ specificity):
 *   1. `navigator.webdriver` — Selenium/Puppeteer/Playwright expose this
 *   2. `navigator.plugins.length === 0` on desktop UA — real Chrome/Edge/
 *      Firefox always have >=1 plugin; headless Chrome has zero
 *   3. Missing `navigator.languages` array — headless browsers often skip
 *   4. `window.chrome` missing but Chrome UA present — headless CDP quirk
 *   5. Notification permission API returns "default" AND Chrome UA — CDP tell
 *
 * Any ONE signal returning true → skip GA4 init and event tracking.
 * We do NOT block the page render — only tracking is suppressed.
 */
function isLikelyBot() {
  if (typeof navigator === "undefined") return false;
  try {
    if (navigator.webdriver === true) return true;
    const ua = navigator.userAgent || "";
    const isChromeUA = /Chrome\//.test(ua) && !/Edg\/|OPR\//.test(ua);
    if (isChromeUA && !window.chrome) return true;
    if (isChromeUA && (navigator.plugins?.length ?? 0) === 0) return true;
    if (!Array.isArray(navigator.languages) && !navigator.languages?.length)
      return true;

    // 2026-08-30 → 2026-09-15 — Timezone-based data-center gate.
    // HavenCostGuide serves US homeowners; large volumes from
    // Asia/Singapore + Asia/Shanghai + Asia/Urumqi are not legitimate
    // and match the pattern of scraper/residential-proxy botnets that
    // leak their machine's real timezone even when the request headers
    // claim en-US locale. We treat these as bots for analytics purposes
    // ONLY — page render, features, and calculators still work; we
    // just don't pollute GA4 with their pings.
    //
    // 2026-09-15 pass 92i — Added mainland China + Hong Kong-adjacent
    // timezones after GSC/GA4 showed 3 Chinese cities in the top-7
    // (Shenzhen, Zhangjiajie, Zhengzhou) plus a 200-user spike on 09-15.
    // Kept intentionally narrow to Asian data-center hubs — real
    // English-language readers in EU/AU/UK still count.
    const CN_TZS = new Set([
      "Asia/Singapore",
      "Asia/Hong_Kong",
      "Asia/Shanghai",     // mainland China (canonical)
      "Asia/Chongqing",    // legacy alias, still leaks from older builds
      "Asia/Urumqi",       // Xinjiang
      "Asia/Kashgar",
      "Asia/Harbin",       // legacy alias
      "Asia/Chungking",    // legacy alias
      "Asia/Macau",
      "Asia/Taipei",       // TW — mostly Chinese-search-hub scraping
    ]);
    const tz =
      (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      "";
    if (CN_TZS.has(tz)) return true;

    // Also flag if the reported HTML lang doesn't match a plausible
    // English-speaking locale — botnets often use `zh-CN` / `ru-RU` on
    // headers meant for en-US content. Only skip GA when combined with
    // (direct) traffic in a future pass; keeping this line commented as
    // reference so it's easy to enable if bot pattern shifts.
    // if (!/^(en|und)/i.test(navigator.language || "")) return true;
  } catch {
    // If any of the above throws, fail OPEN — real users shouldn't have
    // access errors on navigator props; if we can't check, treat as human.
    return false;
  }
  return false;
}

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  if (!GA_ID) return;
  if (isLikelyBot()) return; // headless/CDP — skip tracking silently
  initialized = true;

  // Events queue in dataLayer; gtag.js only loads if the backend doesn't flag a data-center IP.
  fetch(`${API}/visitor-check`, { cache: "no-store" })
    .then((r) => r.status !== 403)
    .catch(() => true)
    .then((ok) => {
      if (!ok) return;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
    });

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: true });
}

export function track(eventName, params = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
  // Dual-write to our backend for first-party analytics. Fire-and-forget;
  // we never block the UI on this and silently swallow network errors.
  if (FIRST_PARTY_EVENTS.has(eventName)) {
    try {
      const body = JSON.stringify({ name: eventName, params });
      // Prefer sendBeacon when available — survives page unloads (the most
      // common time a click event fires, since clicks navigate away).
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(`${API}/events/track`, blob);
      } else {
        fetch(`${API}/events/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // never throw from analytics
    }
  }
}
